import React from 'react'

import {
  ClientHomeAddress,
  SchedulerAvailabilitySource,
  SchedulerOwningEntityType,
  SchedulerParticipant,
} from '@/components/integration/nylas/scheduler/types'
import { MeetingFormat } from '@/types/misc'
import { PublicMeetingTypeLocation } from '@/types/responses/meeting-types'

export interface SchedulerConfigContextValue {
  timezone: string
  setTimezone: (timezone: string) => void
  primaryParticipant: SchedulerParticipant
  owningEntityType: SchedulerOwningEntityType
  owningEntityId: number | string
  availabilitySource: SchedulerAvailabilitySource | undefined
  /**
   * When rescheduling, the participants are fixed so they're not shown or
   * editable. The format and place can still be changed.
   */
  isRescheduling: boolean
  availableMeetingFormats: MeetingFormat[] | undefined
  /** The offices on offer for an in-person office meeting. */
  availableLocations: PublicMeetingTypeLocation[] | undefined
  /** The offices that can handle a meeting at the client's home. */
  availableHomeVisitLocations: PublicMeetingTypeLocation[] | undefined
  /**
   * An address the client gave earlier (e.g. on the client details step), offered
   * as a one-click fill for the home address fields.
   */
  suggestedClientAddress: ClientHomeAddress | null
  /**
   * When rescheduling a meeting at the client's home whose address is hidden
   * from this user: while it stays at the client's home, the address on file
   * is kept, so it isn't asked for.
   */
  hasHiddenClientHomeAddress: boolean
  /**
   * The town or city of the hidden address, when this user may see that much.
   */
  hiddenClientHomeTownOrCity: string | null
  durationMinutes: number | undefined
  maxBookingNoticeDays: number | undefined
}

export const SchedulerConfigContext =
  React.createContext<SchedulerConfigContextValue | null>(null)

export function useSchedulerConfig() {
  const context = React.useContext(SchedulerConfigContext)

  if (context === null) {
    throw new Error(
      'useSchedulerConfig must be used inside a SchedulerConfigContext.Provider',
    )
  }

  return context
}
